import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Loader from "../components/Loader";
import SideNav from "../components/SideNav";
import { format } from "../main";
import moment from "moment";
import { CSVLink } from "react-csv";
import { fetchTerminals } from "../actions/adminActions";
import {
  fetchUnassignedTerminals,
  fetchCustomerBusinesses,
} from "../actions/terminalActions";
import {
  fetchPosRequests,
  retrieveTerminal,
  fetchTerminalSyncStatus,
  fetchSyncDrift,
  updatePosCharges,
  approvePosRequest,
} from "../actions/posActions";

const VERDICT_COLORS = {
  in_sync: "#28a745",
  drift_unmapped: "#fd7e14",
  drift_wrong_account: "#dc3545",
  drift_stale_mapping: "#6f42c1",
  check_failed: "#6c757d",
};

const REQUEST_STATUS = { 1: "Pending", 4: "Withdrawn", 5: "Approved", 7: "Reassigned" };

const POS_PROVIDERS = [
  { id: 17, name: "VFD Microfinance Bank" },
  { id: 18, name: "Parallex / POSVAS" },
  { id: 19, name: "Grupp" },
];

const InfoRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f0f0f0", gap: 8 }}>
    <span style={{ color: "#888", fontSize: 13, minWidth: 140, flexShrink: 0 }}>{label}</span>
    <span style={{ color: "#222", fontSize: 13, fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>{value ?? "—"}</span>
  </div>
);

const TerminalsScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initRef = useRef(true);

  const [activeTab, setActiveTab] = useState("terminals");

  // CSV state for terminals tab
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);

  // Requests tab state
  const [reqFromDate, setReqFromDate] = useState("");
  const [reqToDate, setReqToDate] = useState("");
  const [reqPage, setReqPage] = useState(1);

  // Approve request drawer
  const [approveDrawerOpen, setApproveDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveTerminalId, setApproveTerminalId] = useState("");
  const [approveBusinessId, setApproveBusinessId] = useState("");

  // Retrieve drawer state
  const [retrieveDrawerOpen, setRetrieveDrawerOpen] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [retrieveReason, setRetrieveReason] = useState("");

  // Sync status drawer
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [syncTerminal, setSyncTerminal] = useState(null);

  // Pricing state (one form per provider)
  const [pricingForms, setPricingForms] = useState({
    17: { processor_fee: "", markup: "", cap: "" },
    18: { processor_fee: "", markup: "", cap: "" },
    19: { processor_fee: "", markup: "", cap: "" },
  });
  const [savingPricing, setSavingPricing] = useState(null);

  // Redux selectors
  const admin2faLogin = useSelector((state) => state.admin2faLogin);
  const { userInfo } = admin2faLogin;

  const { loading: terminalsLoading, success: terminalsSuccess, error: terminalsError, terminals } =
    useSelector((state) => state.getTerminals);

  const { loading: requestsLoading, data: requestsData, error: requestsError } =
    useSelector((state) => state.getPosRequests) || {};

  const { loading: unassignedLoading, terminals: unassignedTerminals } =
    useSelector((state) => state.unassignedTerminals) || {};

  const { loading: businessLoading, businesses } =
    useSelector((state) => state.getCustomerBusiness) || {};

  const { loading: retrieveLoading, error: retrieveError } =
    useSelector((state) => state.retrieveTerminal) || {};

  const { loading: syncLoading, data: syncData, error: syncError } =
    useSelector((state) => state.getTerminalSyncStatus) || {};

  const { loading: driftLoading, data: driftData, error: driftError } =
    useSelector((state) => state.getSyncDrift) || {};

  const { loading: approveReqLoading, error: approveReqError } =
    useSelector((state) => state.approvePosRequest) || {};

  useEffect(() => {
    if (userInfo?.user?.role === "Account Officer") {
      navigate(`/officer/${userInfo?.user?.id}`, { replace: true });
    }
    if (initRef.current) {
      dispatch(fetchTerminals());
      initRef.current = false;
    }
    if (terminalsSuccess && terminals) {
      setCsvData(
        terminals.map((t) => ({
          serial_number: t.serialnumber,
          terminal_identity: t.terminal_identity,
          commission: t.commission,
          email_address: t.emailaddress,
          penntag: t.penn_tag,
          merchant_tag: t.merchanttag,
          business_name: t.business_name,
          date_assigned: moment(t.date_created).format("LLL"),
          total_inflow: format(t.total_inflow),
          total_fee: format(t.fee),
          inflow_today: format(t.inflow_today),
          inflow_this_week: format(t.weekly_inflow),
        })),
      );
      setCsvHeaders([
        { label: "Serial Number", key: "serial_number" },
        { label: "Terminal Identity", key: "terminal_identity" },
        { label: "Commission", key: "commission" },
        { label: "Email Address", key: "email_address" },
        { label: "PennTag", key: "penntag" },
        { label: "Merchant Tag", key: "merchant_tag" },
        { label: "Business Name", key: "business_name" },
        { label: "Date Assigned", key: "date_assigned" },
        { label: "Total Inflow", key: "total_inflow" },
        { label: "Total Fee", key: "total_fee" },
        { label: "Inflow Today", key: "inflow_today" },
        { label: "Inflow This Week", key: "inflow_this_week" },
      ]);
    }
  }, [dispatch, terminalsSuccess, terminals, userInfo, navigate]);

  // Tab switch side-effects
  useEffect(() => {
    if (activeTab === "requests") {
      dispatch(fetchPosRequests(1, 50));
    }
    if (activeTab === "reconciliation") {
      dispatch(fetchSyncDrift());
    }
  }, [activeTab, dispatch]);

  // Approve request drawer
  const openApproveDrawer = (req) => {
    setSelectedRequest(req);
    setApproveTerminalId("");
    setApproveBusinessId("");
    dispatch(fetchUnassignedTerminals(1));
    dispatch(fetchCustomerBusinesses(req.customer_id));
    setApproveDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeApproveDrawer = () => {
    setApproveDrawerOpen(false);
    setSelectedRequest(null);
    document.body.style.overflow = "";
  };

  const handleApprovePosRequest = async () => {
    if (!approveTerminalId || !approveBusinessId) {
      toast.error("Please select both a terminal and a business");
      return;
    }
    try {
      const res = await dispatch(
        approvePosRequest(
          Number(approveTerminalId),
          selectedRequest.customer_id,
          Number(approveBusinessId),
          selectedRequest.id,
        ),
      );
      toast.success("Terminal assigned successfully");
      const acct = res?.data;
      if (acct?.account_number) {
        toast.info(`Account: ${acct.account_name} — ${acct.account_number} (${acct.bank})`);
      }
      closeApproveDrawer();
      dispatch(fetchPosRequests(reqPage, 50, reqFromDate, reqToDate));
      dispatch(fetchTerminals());
    } catch (e) {
      toast.error(e.message || "Failed to assign terminal");
    }
  };

  // Retrieve drawer
  const openRetrieveDrawer = (terminal) => {
    setSelectedTerminal(terminal);
    setRetrieveReason("");
    setRetrieveDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeRetrieveDrawer = () => {
    setRetrieveDrawerOpen(false);
    setSelectedTerminal(null);
    document.body.style.overflow = "";
  };

  const handleRetrieve = async () => {
    if (!retrieveReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    try {
      await dispatch(retrieveTerminal(selectedTerminal.terminal_id, retrieveReason));
      toast.success("Terminal retrieved successfully");
      closeRetrieveDrawer();
      dispatch(fetchTerminals());
    } catch (e) {
      toast.error(e.message || "Failed to retrieve terminal");
    }
  };

  // Sync status drawer
  const openSyncDrawer = (terminal) => {
    setSyncTerminal(terminal);
    dispatch({ type: "GET_TERMINAL_SYNC_STATUS_RESET" });
    dispatch(fetchTerminalSyncStatus(terminal.terminal_id, false));
    setSyncDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeSyncDrawer = () => {
    setSyncDrawerOpen(false);
    setSyncTerminal(null);
    document.body.style.overflow = "";
  };

  const handleRepairSync = async (id) => {
    try {
      await dispatch(fetchTerminalSyncStatus(id, true));
      toast.success("Repair attempted — status refreshed");
    } catch (e) {
      toast.error(e.message || "Repair failed");
    }
  };

  // Pricing
  const handlePricingChange = (providerId, field, value) => {
    setPricingForms((prev) => ({
      ...prev,
      [providerId]: { ...prev[providerId], [field]: value },
    }));
  };

  const handleSavePricing = async (providerId) => {
    const form = pricingForms[providerId];
    if (!form.processor_fee || !form.markup || !form.cap) {
      toast.error("All three fields are required");
      return;
    }
    setSavingPricing(providerId);
    try {
      await dispatch(
        updatePosCharges(providerId, {
          processor_fee: Number(form.processor_fee),
          markup: Number(form.markup),
          cap: Number(form.cap),
        }),
      );
      toast.success(`Pricing updated for ${POS_PROVIDERS.find((p) => p.id === providerId)?.name}`);
    } catch (e) {
      toast.error(e.message || "Failed to update pricing");
    } finally {
      setSavingPricing(null);
    }
  };

  const syncResult = syncData?.data || {};
  const driftRows = driftData?.data || [];
  const requests = requestsData?.data?.items || [];
  const reqPagination = requestsData?.data?.page || {};
  const unassigned = unassignedTerminals || [];
  const customerBusinesses = businesses || [];

  const tabStyle = (tab) => ({
    padding: "10px 20px",
    border: "none",
    borderBottom: activeTab === tab ? "3px solid #007bff" : "3px solid transparent",
    background: "none",
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? "#007bff" : "#555",
    cursor: "pointer",
    fontSize: 14,
  });

  const drawerOverlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex",
    justifyContent: "flex-end",
  };

  const drawerPanel = {
    background: "#f4f5f7",
    width: "min(560px, 100vw)",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
  };

  const drawerHeader = {
    background: "#fff",
    padding: "16px 20px",
    borderBottom: "1px solid #e8e8e8",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  };

  return (
    <>
      <SideNav />
      <Helmet>
        <title>Terminals | PennyTree - Admin</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1 style={{ marginBottom: 0 }}>Terminals</h1>

            {/* Tab bar */}
            <div style={{ display: "flex", borderBottom: "1px solid #e0e0e0", marginBottom: 20, marginTop: 12 }}>
              {[
                { key: "terminals", label: "Assigned Terminals" },
                { key: "requests", label: "Requests" },
                { key: "reconciliation", label: "Reconciliation" },
                { key: "pricing", label: "Pricing" },
              ].map((t) => (
                <button key={t.key} style={tabStyle(t.key)} onClick={() => setActiveTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ─── TERMINALS TAB ─── */}
            {activeTab === "terminals" && (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  <CSVLink
                    data={csvData}
                    headers={csvHeaders}
                    filename="terminals.csv"
                    style={{ background: "#28a745", color: "#fff", padding: "7px 16px", borderRadius: 5, fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-block" }}
                  >
                    Export CSV
                  </CSVLink>
                  <Link to="/terminal/add">
                    <button style={{ background: "#007bff", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                      + Add Terminal
                    </button>
                  </Link>
                  <Link to="/terminal/unassigned">
                    <button style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                      Unassigned Terminals
                    </button>
                  </Link>
                </div>

                {terminalsLoading ? (
                  <Loader />
                ) : terminalsError ? (
                  <p style={{ color: "red" }}>{terminalsError}</p>
                ) : (
                  <MDBTable responsive style={{ width: "100%", fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr className="font-reduce">
                        <th>S/N</th>
                        <th>Serial Number</th>
                        <th>Terminal Identity</th>
                        <th>Commission</th>
                        <th>Email Address</th>
                        <th>PennTag</th>
                        <th>Merchant Tag</th>
                        <th>Business Name</th>
                        <th>Date Assigned</th>
                        <th>Total Inflow</th>
                        <th>Total Fee</th>
                        <th>Inflow Today</th>
                        <th>This Week</th>
                        <th>Q1</th>
                        <th>Q2</th>
                        <th>Q3</th>
                        <th>Q4</th>
                        <th>Actions</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {terminalsSuccess &&
                        terminals.map((terminal, key) => (
                          <tr key={key}>
                            <td>{key + 1}</td>
                            <td>{terminal.serialnumber}</td>
                            <td>{terminal.terminal_identity}</td>
                            <td>{terminal.commission}%</td>
                            <td>{terminal.emailaddress}</td>
                            <td>{terminal.penn_tag}</td>
                            <td>{terminal.merchanttag}</td>
                            <td>{terminal.business_name}</td>
                            <td>{moment(terminal.date_created).format("LLL")}</td>
                            <td>{format(terminal.total_inflow)}</td>
                            <td>{format(terminal.fee)}</td>
                            <td>{format(terminal.inflow_today)}</td>
                            <td>{format(terminal.weekly_inflow)}</td>
                            <td>{format(terminal.q1_inflow)}</td>
                            <td>{format(terminal.q2_inflow)}</td>
                            <td>{format(terminal.q3_inflow)}</td>
                            <td>{format(terminal.q4_inflow)}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <Link
                                  to={`/terminal/assign/${terminal.terminal_id}`}
                                  state={{ terminal_id: terminal.terminal_identity, action: "reassign" }}
                                >
                                  <button style={{ background: "#6f42c1", color: "#fff", border: "none", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
                                    Reassign
                                  </button>
                                </Link>
                                <button
                                  onClick={() => openRetrieveDrawer(terminal)}
                                  style={{ background: "#fd7e14", color: "#fff", border: "none", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
                                >
                                  Retrieve
                                </button>
                                <button
                                  onClick={() => openSyncDrawer(terminal)}
                                  style={{ background: "#17a2b8", color: "#fff", border: "none", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
                                >
                                  Sync
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </MDBTableBody>
                  </MDBTable>
                )}
              </>
            )}

            {/* ─── REQUESTS TAB ─── */}
            {activeTab === "requests" && (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div>
                    <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>From</label>
                    <input type="date" value={reqFromDate} onChange={(e) => setReqFromDate(e.target.value)} style={{ padding: "6px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>To</label>
                    <input type="date" value={reqToDate} onChange={(e) => setReqToDate(e.target.value)} style={{ padding: "6px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13 }} />
                  </div>
                  <button
                    onClick={() => { setReqPage(1); dispatch(fetchPosRequests(1, 50, reqFromDate, reqToDate)); }}
                    style={{ background: "#007bff", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}
                  >
                    Filter
                  </button>
                </div>

                {requestsLoading ? (
                  <Loader />
                ) : requestsError ? (
                  <p style={{ color: "red" }}>{requestsError}</p>
                ) : requests.length === 0 ? (
                  <p style={{ color: "#aaa", textAlign: "center", padding: 40 }}>No terminal requests found.</p>
                ) : (
                  <MDBTable responsive style={{ width: "100%", fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr>
                        <th>S/N</th>
                        <th>Business Name</th>
                        <th>Merchant Tag</th>
                        <th>Phone</th>
                        <th>Address</th>
                        <th># Terminals</th>
                        <th>Status</th>
                        <th>Date Requested</th>
                        <th></th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {requests.map((req, i) => (
                        <tr key={req.id}>
                          <td>{i + 1}</td>
                          <td>{req.business_name}</td>
                          <td>{req.merchant_tag}</td>
                          <td>{req.phone}</td>
                          <td>{req.address}</td>
                          <td>{req.number_of_terminal}</td>
                          <td>
                            <span style={{
                              background: req.status_id === 1 ? "#fff3cd" : req.status_id === 5 ? "#d4edda" : "#e2e3e5",
                              color: req.status_id === 1 ? "#856404" : req.status_id === 5 ? "#155724" : "#383d41",
                              padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            }}>
                              {REQUEST_STATUS[req.status_id] || req.status_id}
                            </span>
                          </td>
                          <td>{req.date_requested ? moment(req.date_requested).format("ll") : "—"}</td>
                          <td>
                            {req.status_id === 1 && (
                              <button
                                onClick={() => openApproveDrawer(req)}
                                style={{ background: "#28a745", color: "#fff", border: "none", padding: "5px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                              >
                                Approve
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </MDBTableBody>
                  </MDBTable>
                )}

                {reqPagination.total_page > 1 && (
                  <div style={{ display: "flex", gap: 6, marginTop: 16, justifyContent: "center" }}>
                    {Array.from({ length: reqPagination.total_page }, (_, i) => i + 1).map((p) => (
                      <button key={p} onClick={() => { setReqPage(p); dispatch(fetchPosRequests(p, 50, reqFromDate, reqToDate)); }}
                        style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid #ccc", background: p === reqPage ? "#007bff" : "#fff", color: p === reqPage ? "#fff" : "#333", cursor: "pointer" }}>
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ─── RECONCILIATION TAB ─── */}
            {activeTab === "reconciliation" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                    Shows all assigned terminals that are out of sync with Grupp.
                  </p>
                  <button
                    onClick={() => dispatch(fetchSyncDrift())}
                    disabled={driftLoading}
                    style={{ background: "#007bff", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                  >
                    {driftLoading ? "Checking..." : "Refresh Drift Report"}
                  </button>
                </div>

                {driftLoading ? (
                  <Loader />
                ) : driftError ? (
                  <p style={{ color: "red" }}>{driftError}</p>
                ) : driftRows.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 48, color: "#28a745", fontWeight: 600, fontSize: 15 }}>
                    ✓ All assigned terminals are in sync
                  </div>
                ) : (
                  <MDBTable responsive style={{ fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr>
                        <th>Terminal ID</th>
                        <th>Identity</th>
                        <th>Serial</th>
                        <th>Verdict</th>
                        <th>Grupp Account</th>
                        <th>Local Account</th>
                        <th>Action</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {driftRows.map((row) => (
                        <tr key={row.terminal_id}>
                          <td>{row.terminal_id}</td>
                          <td>{row.terminal_identity}</td>
                          <td>{row.serial_number}</td>
                          <td>
                            <span style={{
                              background: VERDICT_COLORS[row.verdict] || "#6c757d",
                              color: "#fff",
                              padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            }}>
                              {row.verdict?.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td>{row.grupp_account?.account_number || "—"}</td>
                          <td>{row.local_account_number || "—"}</td>
                          <td>
                            {row.verdict !== "drift_stale_mapping" && (
                              <button
                                onClick={() => handleRepairSync(row.terminal_id)}
                                style={{ background: "#fd7e14", color: "#fff", border: "none", padding: "4px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                              >
                                Repair
                              </button>
                            )}
                            {row.verdict === "drift_stale_mapping" && (
                              <span style={{ fontSize: 11, color: "#888" }}>Manual review required</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </MDBTableBody>
                  </MDBTable>
                )}
              </>
            )}

            {/* ─── PRICING TAB ─── */}
            {activeTab === "pricing" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20, maxWidth: 960 }}>
                {POS_PROVIDERS.map((provider) => (
                  <div key={provider.id} style={{ background: "#fff", borderRadius: 8, border: "1px solid #e0e0e0", padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#333", marginBottom: 4 }}>{provider.name}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 16 }}>Provider ID: {provider.id}</div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Processor Fee</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 0.50"
                        value={pricingForms[provider.id].processor_fee}
                        onChange={(e) => handlePricingChange(provider.id, "processor_fee", e.target.value)}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Markup (Deposit Fee)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 0.20"
                        value={pricingForms[provider.id].markup}
                        onChange={(e) => handlePricingChange(provider.id, "markup", e.target.value)}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Cap (₦)</label>
                      <input
                        type="number"
                        placeholder="e.g. 300"
                        value={pricingForms[provider.id].cap}
                        onChange={(e) => handlePricingChange(provider.id, "cap", e.target.value)}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                      />
                    </div>
                    <button
                      onClick={() => handleSavePricing(provider.id)}
                      disabled={savingPricing === provider.id}
                      style={{ width: "100%", background: "#007bff", color: "#fff", border: "none", padding: "9px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                    >
                      {savingPricing === provider.id ? "Saving..." : "Save Pricing"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── APPROVE REQUEST DRAWER ─── */}
      {approveDrawerOpen && (
        <div style={drawerOverlay} onClick={(e) => e.target === e.currentTarget && closeApproveDrawer()}>
          <div style={drawerPanel}>
            <div style={drawerHeader}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Approve Terminal Request</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{selectedRequest?.business_name}</div>
              </div>
              <button onClick={closeApproveDrawer} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#777" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {/* Request details */}
              <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: "14px 16px", marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#444", marginBottom: 10, textTransform: "uppercase" }}>Request Details</div>
                <InfoRow label="Business" value={selectedRequest?.business_name} />
                <InfoRow label="Merchant Tag" value={selectedRequest?.merchant_tag} />
                <InfoRow label="Phone" value={selectedRequest?.phone} />
                <InfoRow label="Address" value={selectedRequest?.address} />
                <InfoRow label="Terminals Requested" value={selectedRequest?.number_of_terminal} />
                <InfoRow label="Support Email" value={selectedRequest?.support_email_address} />
                <InfoRow label="Support Phone" value={selectedRequest?.support_mobile_number} />
              </div>

              {/* Terminal selector */}
              <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: "14px 16px", marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#444", marginBottom: 10, textTransform: "uppercase" }}>Assign Terminal</div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Select Unassigned Terminal</label>
                  {unassignedLoading ? (
                    <p style={{ fontSize: 13, color: "#888" }}>Loading terminals...</p>
                  ) : (
                    <select
                      value={approveTerminalId}
                      onChange={(e) => setApproveTerminalId(e.target.value)}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13 }}
                    >
                      <option value="">— Select terminal —</option>
                      {unassigned.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.terminal_identity} ({t.serialnumber})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Select Business</label>
                  {businessLoading ? (
                    <p style={{ fontSize: 13, color: "#888" }}>Loading businesses...</p>
                  ) : (
                    <select
                      value={approveBusinessId}
                      onChange={(e) => setApproveBusinessId(e.target.value)}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13 }}
                    >
                      <option value="">— Select business —</option>
                      {customerBusinesses.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.business_name || b.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {approveReqError && <p style={{ color: "red", fontSize: 13 }}>{approveReqError}</p>}

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleApprovePosRequest}
                  disabled={approveReqLoading || !approveTerminalId || !approveBusinessId}
                  style={{ flex: 1, background: "#28a745", color: "#fff", border: "none", padding: "10px", borderRadius: 5, cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                >
                  {approveReqLoading ? "Assigning..." : "Confirm Assignment"}
                </button>
                <button onClick={closeApproveDrawer} style={{ background: "#6c757d", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 5, cursor: "pointer", fontSize: 14 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── RETRIEVE DRAWER ─── */}
      {retrieveDrawerOpen && (
        <div style={drawerOverlay} onClick={(e) => e.target === e.currentTarget && closeRetrieveDrawer()}>
          <div style={{ ...drawerPanel, width: "min(420px, 100vw)" }}>
            <div style={drawerHeader}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Retrieve Terminal</div>
              <button onClick={closeRetrieveDrawer} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#777" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: "14px 16px", marginBottom: 16 }}>
                <InfoRow label="Terminal" value={selectedTerminal?.terminal_identity} />
                <InfoRow label="Serial" value={selectedTerminal?.serialnumber} />
                <InfoRow label="Merchant" value={selectedTerminal?.merchanttag} />
                <InfoRow label="Business" value={selectedTerminal?.business_name} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>
                  Reason for retrieval <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                  value={retrieveReason}
                  onChange={(e) => setRetrieveReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. merchant closed shop"
                  style={{ width: "100%", padding: "8px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
                />
              </div>
              {retrieveError && <p style={{ color: "red", fontSize: 13 }}>{retrieveError}</p>}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleRetrieve}
                  disabled={retrieveLoading}
                  style={{ flex: 1, background: "#fd7e14", color: "#fff", border: "none", padding: "10px", borderRadius: 5, cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                >
                  {retrieveLoading ? "Processing..." : "Confirm Retrieve"}
                </button>
                <button onClick={closeRetrieveDrawer} style={{ background: "#6c757d", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 5, cursor: "pointer", fontSize: 14 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── SYNC STATUS DRAWER ─── */}
      {syncDrawerOpen && (
        <div style={drawerOverlay} onClick={(e) => e.target === e.currentTarget && closeSyncDrawer()}>
          <div style={{ ...drawerPanel, width: "min(460px, 100vw)" }}>
            <div style={drawerHeader}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Sync Status</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{syncTerminal?.terminal_identity}</div>
              </div>
              <button onClick={closeSyncDrawer} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#777" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {syncLoading ? (
                <p style={{ textAlign: "center", padding: 40, color: "#888" }}>Checking sync status...</p>
              ) : syncError ? (
                <p style={{ color: "red" }}>{syncError}</p>
              ) : (
                <>
                  <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ textAlign: "center", marginBottom: 12 }}>
                      <span style={{
                        background: VERDICT_COLORS[syncResult.verdict] || "#6c757d",
                        color: "#fff",
                        padding: "4px 16px",
                        borderRadius: 14,
                        fontSize: 13,
                        fontWeight: 700,
                      }}>
                        {syncResult.verdict?.replace(/_/g, " ") || "—"}
                      </span>
                      {syncResult.repaired && (
                        <span style={{ marginLeft: 8, background: "#d4edda", color: "#155724", padding: "4px 10px", borderRadius: 10, fontSize: 12 }}>
                          Repaired
                        </span>
                      )}
                    </div>
                    <InfoRow label="Terminal Identity" value={syncResult.terminal_identity} />
                    <InfoRow label="Serial Number" value={syncResult.serial_number} />
                    <InfoRow label="Mapped at Grupp" value={syncResult.is_mapped_at_grupp ? "Yes" : "No"} />
                    <InfoRow label="Grupp Account Name" value={syncResult.grupp_account?.account_name} />
                    <InfoRow label="Grupp Account No." value={syncResult.grupp_account?.account_number} />
                    <InfoRow label="Grupp Bank" value={syncResult.grupp_account?.bank} />
                    <InfoRow label="Local Account No." value={syncResult.local_account_number} />
                  </div>

                  {syncResult.verdict && syncResult.verdict !== "in_sync" && syncResult.verdict !== "drift_stale_mapping" && (
                    <button
                      onClick={() => handleRepairSync(syncTerminal.terminal_id)}
                      disabled={syncLoading}
                      style={{ width: "100%", background: "#fd7e14", color: "#fff", border: "none", padding: "10px", borderRadius: 5, cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                    >
                      {syncLoading ? "Repairing..." : "Repair Sync"}
                    </button>
                  )}
                  {syncResult.verdict === "drift_stale_mapping" && (
                    <p style={{ fontSize: 12, color: "#888", textAlign: "center" }}>
                      Stale mapping — may be routing a live merchant's settlements. Manual review required before auto-repair.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TerminalsScreen;
